import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Checkbox,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import { syslogService, firewallService } from '../services/api';
import { formatLocalDateTime } from '../utils/date';

// Helper function to get display name for protocol
const getProtocolDisplayName = (protocol) => {
  const protocolMap = {
    'UDP': 'UDP',
    'TCP': 'TCP',
    'WAZUH_AGENT': 'Wazuh (nginx)',
    'WAZUH_AGENT_NSCAT_LOG': 'Wazuh (nscat)'
  };
  return protocolMap[protocol] || protocol;
};

// Helper to derive alert level text from audit score (mirror backend logic)
const getAlertLevelFromScore = (score) => {
  if (score === null || score === undefined || isNaN(Number(score))) {
    return null;
  }
  const value = Number(score);
  if (value < 30) return 'Critical Alert';
  if (value < 50) return 'High Alert';
  if (value < 80) return 'Medium Alert';
  if (value < 96) return 'Low Alert';
  return 'Compliant';
};

const getAlertChipStyle = (alertLevel) => {
  switch (alertLevel) {
    case 'Critical Alert':
      return { backgroundColor: '#d32f2f', color: '#fff' };
    case 'High Alert':
      return { backgroundColor: '#f57c00', color: '#fff' };
    case 'Medium Alert':
      return { backgroundColor: '#fbc02d', color: 'rgba(0,0,0,0.87)' };
    case 'Low Alert':
      return { backgroundColor: '#388e3c', color: '#fff' };
    case 'Compliant':
      return { backgroundColor: '#0d47a1', color: '#fff' };
    default:
      return { backgroundColor: '#616161', color: '#fff' };
  }
};

function Syslog() {
  // Configuration state
  const [syslogConfig, setSyslogConfig] = useState({
    serverIp: '',
    serverPort: 514,
    protocol: 'UDP'
  });
  
  // Remember last IP for TCP/UDP protocols
  const [lastTcpUdpIp, setLastTcpUdpIp] = useState(() => {
    const saved = localStorage.getItem('nscat_last_tcp_udp_ip');
    return saved || '';
  });
  
  // Store saved config from database to check for TCP/UDP IP
  const [savedConfigFromDb, setSavedConfigFromDb] = useState(null);

  const isLocalWazuhAgentProtocol = (protocol) =>
    protocol === 'WAZUH_AGENT' || protocol === 'WAZUH_AGENT_NSCAT_LOG';

  // Audit selection state
  const [selectedAuditId, setSelectedAuditId] = useState('');
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingAudits, setLoadingAudits] = useState(false);

  // Syslog history state
  const [syslogHistory, setSyslogHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    total: 0,
    totalPages: 0
  });
  const [selectedLogs, setSelectedLogs] = useState([]);

  // UI state
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, entry: null });
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    protocol: '',
    firewallId: '',
    firewallIp: '',
    alertType: '',
    auditScoreMin: '',
    auditScoreMax: ''
  });

  // Fetch saved configuration and audit history on mount
  useEffect(() => {
    fetchSyslogConfig();
    fetchAuditHistory();
  }, []);

  // Fetch syslog history
  useEffect(() => {
    fetchSyslogHistory();
  }, [pagination.page, filters]);

  const fetchSyslogConfig = async () => {
    try {
      const config = await syslogService.getSyslogConfig();
      // Always update config if we get a response
      // This ensures saved WAZUH_AGENT config is loaded correctly
      if (config) {
        const loadedConfig = {
          serverIp: config.syslog_server_ip || '',
          serverPort: config.syslog_server_port || 514,
          protocol: config.protocol || 'UDP'
        };
        console.log('Loaded syslog config:', loadedConfig);
        setSyslogConfig(loadedConfig);
        
        // Store the full config from database for reference
        setSavedConfigFromDb(config);
        
        // Remember IP for TCP/UDP protocols when loading config
        if (loadedConfig.protocol === 'TCP' || loadedConfig.protocol === 'UDP') {
          if (loadedConfig.serverIp) {
            setLastTcpUdpIp(loadedConfig.serverIp);
            localStorage.setItem('nscat_last_tcp_udp_ip', loadedConfig.serverIp);
          }
        }
      } else {
        // If no config exists, keep defaults (UDP)
        console.log('No saved syslog config found, using defaults');
      }
    } catch (err) {
      console.error('Error fetching syslog config:', err);
      // Don't show error to user - just use defaults
    }
  };

  const handleSaveConfig = async () => {
    // Validate configuration
    if (!isLocalWazuhAgentProtocol(syslogConfig.protocol) && !syslogConfig.serverIp) {
      setSnackbar({
        open: true,
        message: 'Please enter syslog server IP address',
        severity: 'error'
      });
      return;
    }

    try {
      const savedConfig = await syslogService.saveSyslogConfig(syslogConfig);
      console.log('Saved syslog config:', savedConfig);
      // Update local state with saved config to ensure consistency
      if (savedConfig) {
        const updatedConfig = {
          serverIp: savedConfig.syslog_server_ip || '',
          serverPort: savedConfig.syslog_server_port || 514,
          protocol: savedConfig.protocol || 'UDP'
        };
        setSyslogConfig(updatedConfig);
        
        // Store the full config from database for reference
        setSavedConfigFromDb(savedConfig);
        
        // Remember IP for TCP/UDP protocols
        if (savedConfig.protocol === 'TCP' || savedConfig.protocol === 'UDP') {
          if (savedConfig.syslog_server_ip) {
            setLastTcpUdpIp(savedConfig.syslog_server_ip);
            localStorage.setItem('nscat_last_tcp_udp_ip', savedConfig.syslog_server_ip);
          }
        }
      }
      setSnackbar({
        open: true,
        message: 'Syslog configuration saved successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving syslog config:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to save syslog configuration',
        severity: 'error'
      });
    }
  };

  const fetchAuditHistory = async () => {
    try {
      setLoadingAudits(true);
      const response = await firewallService.getAuditHistory({
        page: 1,
        page_size: 100 // Get recent audits for selection
      });
      const audits = response.audits || [];
      setAuditHistory(audits);
      if (audits.length > 0 && !selectedAuditId) {
        setSelectedAuditId(audits[0].id);
      }
    } catch (err) {
      console.error('Error fetching audit history:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load audit history',
        severity: 'error'
      });
    } finally {
      setLoadingAudits(false);
    }
  };

  const fetchSyslogHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await syslogService.getSyslogHistory(
        pagination.page,
        pagination.pageSize,
        filters
      );
      setSyslogHistory(response.results || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        totalPages: response.total_pages || 0
      }));
      setSelectedLogs([]);
    } catch (err) {
      console.error('Error fetching syslog history:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load syslog history',
        severity: 'error'
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleToggleSelect = (logId) => {
    setSelectedLogs(prev =>
      prev.includes(logId) ? prev.filter(id => id !== logId) : [...prev, logId]
    );
  };

  const handleToggleSelectAll = () => {
    if (syslogHistory.length === 0) {
      return;
    }
    if (selectedLogs.length === syslogHistory.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(syslogHistory.map(entry => entry.id));
    }
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this log entry?')) {
      return;
    }
    try {
      const response = await syslogService.deleteSyslog(logId);
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Syslog entry deleted successfully',
          severity: 'success'
        });
        fetchSyslogHistory();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to delete syslog entry',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error deleting syslog entry:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete syslog entry',
        severity: 'error'
      });
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedLogs.length) {
      return;
    }
    if (!window.confirm('Are you sure you want to delete the selected log entries?')) {
      return;
    }
    try {
      const response = await syslogService.bulkDeleteSyslogs(selectedLogs);
      if (response.success) {
        const deletedCount = response.deleted ?? selectedLogs.length;
        setSnackbar({
          open: true,
          message: `Deleted ${deletedCount} syslog entr${deletedCount === 1 ? 'y' : 'ies'}`,
          severity: 'success'
        });
        fetchSyslogHistory();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to delete selected syslog entries',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error deleting selected syslog entries:', err);
      setSnackbar({
        open: true,
        message: 'Failed to delete selected syslog entries',
        severity: 'error'
      });
    }
  };

  const handleSendSyslog = async () => {
    // Validate configuration
    if (!isLocalWazuhAgentProtocol(syslogConfig.protocol) && !syslogConfig.serverIp) {
      setSnackbar({
        open: true,
        message: 'Please enter syslog server IP address',
        severity: 'error'
      });
      return;
    }

    try {
      setSending(true);
      // Debug: Log the protocol being sent
      console.log('Sending test log with protocol:', syslogConfig.protocol);
      console.log('Full syslogConfig:', syslogConfig);
      
      // Send test log with current timestamp (no audit selection needed)
      const response = await syslogService.sendTestLog(syslogConfig);
      
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Test log sent successfully',
          severity: 'success'
        });
        // Refresh history
        fetchSyslogHistory();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to send test log',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error sending test log:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || 'Failed to send test log',
        severity: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (logId) => {
    try {
      const response = await syslogService.resendSyslog(logId);
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Syslog message resent successfully',
          severity: 'success'
        });
        fetchSyslogHistory();
      } else {
        setSnackbar({
          open: true,
          message: response.message || 'Failed to resend syslog message',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error resending syslog:', err);
      setSnackbar({
        open: true,
        message: 'Failed to resend syslog message',
        severity: 'error'
      });
    }
  };

  const handleViewDetails = async (logId) => {
    try {
      const entry = await syslogService.getSyslogEntry(logId);
      setDetailsDialog({ open: true, entry });
    } catch (err) {
      console.error('Error fetching syslog entry:', err);
      setSnackbar({
        open: true,
        message: 'Failed to load syslog entry details',
        severity: 'error'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        SIEM Integration
      </Typography>

      {/* Configuration Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={!isLocalWazuhAgentProtocol(syslogConfig.protocol) && !syslogConfig.serverIp}
          >
            Save Configuration
          </Button>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={syslogConfig.protocol}
                onChange={(e) => {
                  const newProtocol = e.target.value;
                  const isTcpUdp = newProtocol === 'TCP' || newProtocol === 'UDP';
                  const isLocalWazuh = newProtocol === 'WAZUH_AGENT' || newProtocol === 'WAZUH_AGENT_NSCAT_LOG';
                  
                  // If switching to TCP/UDP, restore last saved IP
                  // If switching away from TCP/UDP, save current IP
                  if (isTcpUdp) {
                    // Restore last IP for TCP/UDP
                    // Priority: 1) localStorage, 2) saved config from DB (if it was TCP/UDP), 3) current value
                    let ipToRestore = lastTcpUdpIp;
                    
                    // If localStorage is empty, check if saved config from DB has a valid IP
                    // (might have been saved as TCP/UDP before switching to Wazuh Agent)
                    if (!ipToRestore && savedConfigFromDb) {
                      // Check if the saved config has a valid IP (not Wazuh Agent placeholder)
                      const savedIp = savedConfigFromDb.syslog_server_ip;
                      if (savedIp && 
                          savedIp !== 'WAZUH_AGENT' && 
                          savedIp !== 'WAZUH_AGENT_NSCAT_LOG' &&
                          savedIp.trim() !== '') {
                        ipToRestore = savedIp;
                        // Also update localStorage for future use
                        setLastTcpUdpIp(savedIp);
                        localStorage.setItem('nscat_last_tcp_udp_ip', savedIp);
                      }
                    }
                    
                    // Fallback to current value if nothing found
                    if (!ipToRestore) {
                      ipToRestore = syslogConfig.serverIp;
                    }
                    
                    setSyslogConfig({
                      ...syslogConfig,
                      protocol: newProtocol,
                      serverIp: ipToRestore
                    });
                  } else if (isLocalWazuh) {
                    // Save current IP before switching to local Wazuh agent
                    if (syslogConfig.serverIp && (syslogConfig.protocol === 'TCP' || syslogConfig.protocol === 'UDP')) {
                      setLastTcpUdpIp(syslogConfig.serverIp);
                      localStorage.setItem('nscat_last_tcp_udp_ip', syslogConfig.serverIp);
                    }
                    setSyslogConfig({
                      ...syslogConfig,
                      protocol: newProtocol,
                      serverIp: '' // Clear IP for local Wazuh agent
                    });
                  } else {
                    setSyslogConfig({ ...syslogConfig, protocol: newProtocol });
                  }
                }}
                label="Protocol"
              >
                <MenuItem value="UDP">UDP</MenuItem>
                <MenuItem value="TCP">TCP</MenuItem>
                <MenuItem value="WAZUH_AGENT_NSCAT_LOG">Wazuh (nscat)</MenuItem>
                <MenuItem value="WAZUH_AGENT">Wazuh (nginx)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {!isLocalWazuhAgentProtocol(syslogConfig.protocol) && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Syslog Server IP"
                  value={syslogConfig.serverIp}
                  onChange={(e) => setSyslogConfig({ ...syslogConfig, serverIp: e.target.value })}
                  placeholder="192.168.1.100"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Port"
                  type="number"
                  value={syslogConfig.serverPort}
                  onChange={(e) => setSyslogConfig({ ...syslogConfig, serverPort: parseInt(e.target.value) || 514 })}
                  inputProps={{ min: 1, max: 65535 }}
                  required
                />
              </Grid>
            </>
          )}
          {syslogConfig.protocol === 'WAZUH_AGENT' && (
            <Grid item xs={12} sm={8}>
              <Alert severity="info">
                Test logs will be written to the existing web access log (usually /var/log/nginx/access.log) and monitored by the Wazuh agent.
              </Alert>
            </Grid>
          )}
          {syslogConfig.protocol === 'WAZUH_AGENT_NSCAT_LOG' && (
            <Grid item xs={12} sm={8}>
              <Alert severity="info">
                Test logs will be written to /var/log/nscat/nscat-audit.log (JSON line) and monitored by the Wazuh agent.
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Test Log Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Test Log
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={8}>
            <Alert severity="info">
              Send a test log message with current system timestamp to verify syslog configuration.
            </Alert>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSendSyslog}
              disabled={sending || (!isLocalWazuhAgentProtocol(syslogConfig.protocol) && !syslogConfig.serverIp)}
              sx={{ height: '56px' }}
            >
              {sending ? 'Sending...' : 'Send Test Log'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Syslog History Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Log History
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchSyslogHistory}
              disabled={loadingHistory}
            >
              Refresh
            </Button>
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedLogs.length === 0}
              color="error"
            >
              Delete Selected
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Alert Type</InputLabel>
              <Select
                value={filters.alertType}
                onChange={(e) => setFilters({ ...filters, alertType: e.target.value })}
                label="Alert Type"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Critical Alert">Critical Alert</MenuItem>
                <MenuItem value="High Alert">High Alert</MenuItem>
                <MenuItem value="Medium Alert">Medium Alert</MenuItem>
                <MenuItem value="Low Alert">Low Alert</MenuItem>
                <MenuItem value="Compliant">Compliant</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={filters.protocol}
                onChange={(e) => setFilters({ ...filters, protocol: e.target.value })}
                label="Protocol"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="UDP">UDP</MenuItem>
                <MenuItem value="TCP">TCP</MenuItem>
                <MenuItem value="WAZUH_AGENT_NSCAT_LOG">Wazuh (nscat)</MenuItem>
                <MenuItem value="WAZUH_AGENT">Wazuh (nginx)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Firewall IP"
              value={filters.firewallIp}
              onChange={(e) => setFilters({ ...filters, firewallIp: e.target.value })}
              placeholder="e.g. 192.168.86.204"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Min Score"
              type="number"
              value={filters.auditScoreMin}
              onChange={(e) => setFilters({ ...filters, auditScoreMin: e.target.value })}
              inputProps={{ min: 0, max: 100 }}
              placeholder="0"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Max Score"
              type="number"
              value={filters.auditScoreMax}
              onChange={(e) => setFilters({ ...filters, auditScoreMax: e.target.value })}
              inputProps={{ min: 0, max: 100 }}
              placeholder="100"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setFilters({
                startDate: '',
                endDate: '',
                protocol: '',
                firewallId: '',
                firewallIp: '',
                alertType: '',
                auditScoreMin: '',
                auditScoreMax: ''
              })}
              sx={{ height: '56px' }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>

        {/* History Table */}
        {loadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : syslogHistory.length === 0 ? (
          <Alert severity="info">No syslog history found</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedLogs.length > 0 && selectedLogs.length < syslogHistory.length}
                        checked={syslogHistory.length > 0 && selectedLogs.length === syslogHistory.length}
                        onChange={handleToggleSelectAll}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>Audit Time</TableCell>
                    <TableCell>Firewall IP</TableCell>
                    <TableCell>Device Name</TableCell>
                    <TableCell>Score</TableCell>
                    <TableCell>Alert</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', pr: 1 }}>Syslog Server</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', pl: 1 }}>Protocol</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syslogHistory.map((entry) => {
                    // Parse message_content to extract values, with fallback to relationship data
                    let deviceName = entry.firewall?.device_name || 'Unknown';
                    let firewallIp = entry.firewall?.ip_address || 'N/A';
                    let auditScore = entry.audit?.overall_score;
                    let auditTime = entry.audit?.audit_date;
                    let alertLevel = entry.alert_type || null;
                    
                    // Try to parse message_content - could be JSON or plain text syslog message
                    if (entry.message_content) {
                      try {
                        // First try parsing as JSON (for WAZUH_AGENT_NSCAT_LOG)
                        const messageData = JSON.parse(entry.message_content);
                        if (messageData.device_name) {
                          deviceName = messageData.device_name;
                        }
                        if (messageData.ip_address) {
                          firewallIp = messageData.ip_address;
                        }
                        if (messageData.audit_score !== undefined && messageData.audit_score !== null) {
                          auditScore = messageData.audit_score;
                        }
                        if (messageData.audit_time) {
                          auditTime = messageData.audit_time;
                        }
                        if (!alertLevel && messageData.alert_type) {
                          alertLevel = messageData.alert_type;
                        }
                      } catch (e) {
                        // If JSON parsing fails, try to extract from plain text syslog message
                        // Format examples:
                        // RFC 3164: "<134>Dec 19 20:30:45 VPS1 NSCAT: Firewall audit - Device: TEST-DEVICE-123, IP: 192.168.1.45, Score: 67.3%, Medium Alert"
                        // RFC 5424: "<134>1 2025-12-19T20:30:45.123+05:00 VPS1 NSCAT - - [NSCAT-AUDIT...] Firewall audit: TEST-DEVICE-123 - Score: 67.3%, Medium Alert"
                        // Web log: "192.168.1.45 - - [19/Dec/2025:20:30:45 +0500] \"GET /nscat/audit?device=TEST-DEVICE-123&ip=192.168.1.45&score=67.3 HTTP/1.1\" 200 0"
                        const messageText = entry.message_content;
                        
                        // Extract device name - try multiple patterns
                        let deviceMatch = messageText.match(/Device:\s*([^,]+)/i);
                        if (!deviceMatch) {
                          deviceMatch = messageText.match(/audit:\s*([^-]+)/i);
                        }
                        if (!deviceMatch) {
                          deviceMatch = messageText.match(/device=([^&]+)/i);
                        }
                        if (deviceMatch) {
                          deviceName = decodeURIComponent(deviceMatch[1].trim());
                        }
                        
                        // Extract IP address - try multiple patterns
                        let ipMatch = messageText.match(/IP:\s*([^,]+)/i);
                        if (!ipMatch) {
                          ipMatch = messageText.match(/ip=([^&\s]+)/i);
                        }
                        if (!ipMatch) {
                          // Try to extract from web log format (IP at the start)
                          ipMatch = messageText.match(/^(\d+\.\d+\.\d+\.\d+)/);
                        }
                        if (ipMatch) {
                          firewallIp = decodeURIComponent(ipMatch[1].trim());
                        }
                        
                        // Extract audit score - try multiple patterns
                        // Pattern 1: "Score: XX.X%" (RFC 3164/5424)
                        let scoreMatch = messageText.match(/Score:\s*([\d.]+)%?/i);
                        // Pattern 2: "score=XX.X" (web log format)
                        if (!scoreMatch) {
                          scoreMatch = messageText.match(/score=([\d.]+)/i);
                        }
                        // Pattern 3: "score=\"XX.X\"" (RFC 5424 structured data)
                        if (!scoreMatch) {
                          scoreMatch = messageText.match(/score="([\d.]+)"/i);
                        }
                        if (scoreMatch) {
                          const scoreValue = parseFloat(scoreMatch[1]);
                          if (!isNaN(scoreValue)) {
                            auditScore = scoreValue;
                          }
                        }
                        
                        // Extract audit time if present in message
                        // (usually not in plain text syslog, but check anyway)
                        const timeMatch = messageText.match(/audit_time["\s:]+([^",\s]+)/i);
                        if (timeMatch) {
                          auditTime = timeMatch[1];
                        }

                        // Extract alert level text from plain message if present
                        if (!alertLevel) {
                          const alertMatch = messageText.match(/(Critical Alert|High Alert|Medium Alert|Low Alert|Compliant)/i);
                          if (alertMatch) {
                            alertLevel = alertMatch[1];
                          }
                        }
                      }
                    }

                    // Derive alert level from audit score if still unknown
                    if (!alertLevel && auditScore !== null && auditScore !== undefined && !isNaN(Number(auditScore))) {
                      alertLevel = getAlertLevelFromScore(auditScore);
                    }
                    
                    return (
                    <TableRow key={entry.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedLogs.includes(entry.id)}
                          onChange={() => handleToggleSelect(entry.id)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                          {auditTime ? formatLocalDateTime(auditTime) : 'N/A'}
                      </TableCell>
                      <TableCell>{firewallIp}</TableCell>
                      <TableCell>{deviceName}</TableCell>
                      <TableCell>
                          {auditScore !== null && auditScore !== undefined
                            ? `${auditScore.toFixed(1)}%`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                          {alertLevel
                            ? <Chip label={alertLevel} size="small" sx={{ ...getAlertChipStyle(alertLevel), fontWeight: 600, fontSize: '0.7rem' }} />
                            : 'N/A'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', pr: 1 }}>
                        {entry.syslog_server_ip}:{entry.syslog_server_port}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', pl: 1 }}>
                        {getProtocolDisplayName(entry.protocol)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.status}
                          color={getStatusColor(entry.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title="View Log">
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            onClick={() => handleViewDetails(entry.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {entry.status === 'failed' && (
                          <Tooltip title="Resend">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => handleResend(entry.id)}
                              color="primary"
                            >
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {selectedLogs.includes(entry.id) && (
                          <Tooltip title="Delete Log">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => handleDeleteLog(entry.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={(e, page) => setPagination({ ...pagination, page })}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, entry: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {detailsDialog.entry && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Full Log Message:</Typography>
                <Tooltip title="Copy log">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const fullLog = detailsDialog.entry.message_content || 'N/A';
                      navigator.clipboard.writeText(fullLog).then(() => {
                        setSnackbar({
                          open: true,
                          message: 'Log message copied to clipboard',
                          severity: 'success'
                        });
                      }).catch(() => {
                        setSnackbar({
                          open: true,
                          message: 'Failed to copy log message',
                          severity: 'error'
                        });
                      });
                    }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <Paper sx={{ p: 2, bgcolor: '#0a0e1a', fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', border: '1px solid rgba(148, 163, 184, 0.12)' }}>
                {detailsDialog.entry.message_content || 'N/A'}
              </Paper>
              {detailsDialog.entry.error_message && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Error Message:</Typography>
                  <Alert severity="error">{detailsDialog.entry.error_message}</Alert>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog({ open: false, entry: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
}

export default Syslog;
